// UA-Browse-Onboarding-Assistent (#152).
//
// Zwei Schritte: (1) OPC-UA-Endpunkt eingeben und den Server read-only browsen; (2) die gebrowsten
// Tag-Kandidaten (mit NodeId, Datentyp, Richtung, OPC-Bindung) je Zeile bestätigen oder verwerfen
// und den bestätigten Signalkatalog schreiben. Nichts geht ungeprüft produktiv — jeder Kandidat ist
// niedrig-konfident und human-bestätigt; das Browse schreibt nichts an die Anlage (read-only).
//
// Muster wie das foto-gestützte Onboarding (#183) und der passive Tap (#167): Draft → human-Review
// → Confirm. Der Mehrwert gegenüber dem passiven Tap: die OPC-Bindung (node_id/datatype/access)
// steht schon im Entwurf, weil ein Browse die NodeId liefert.

import { useCallback, useMemo, useState, type JSX } from 'react';
import { useI18n } from '../i18n';
import { uaBrowseConfirm, uaBrowseDraft } from '../api/client';
import type { UaBrowseDecision, UaBrowseDraftResult } from '../api/types';
import { Button, LabeledInput, PanelHeading } from '../components/ui';

export interface UaBrowseOnboardingDialogProps {
  projectId: string;
  onClose: () => void;
  // Nach erfolgreichem Confirm: der Aufrufer lädt den Katalog neu (rev als Info).
  onConfirmed: (rev: string) => void | Promise<void>;
}

export function UaBrowseOnboardingDialog({
  projectId,
  onClose,
  onConfirmed,
}: UaBrowseOnboardingDialogProps): JSX.Element {
  const { t } = useI18n();

  const [endpoint, setEndpoint] = useState('opc.tcp://');
  const [draft, setDraft] = useState<UaBrowseDraftResult | null>(null);
  // Entscheidung je Tag: true = bestätigt, false = verworfen. Startzustand: alle bestätigt
  // (der Betreiber kuratiert, verwirft gezielt) — bewusst als Vorschlag, nicht als Zwang.
  const [confirmed, setConfirmed] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedRev, setSavedRev] = useState<string | null>(null);

  const runBrowse = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await uaBrowseDraft(projectId, endpoint.trim());
      setDraft(res);
      const initial: Record<string, boolean> = {};
      for (const v of res.catalog.variables) initial[v.tag] = true;
      setConfirmed(initial);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [projectId, endpoint]);

  const toggle = useCallback((tag: string) => {
    setConfirmed((prev) => ({ ...prev, [tag]: !prev[tag] }));
  }, []);

  const confirmedCount = useMemo(
    () => Object.values(confirmed).filter(Boolean).length,
    [confirmed],
  );

  const runConfirm = useCallback(async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      const decisions: UaBrowseDecision[] = draft.catalog.variables.map((v) => ({
        tag: v.tag,
        confirmed: confirmed[v.tag] ?? false,
      }));
      const res = await uaBrowseConfirm(projectId, draft.catalog, decisions);
      setSavedRev(res.rev);
      await onConfirmed(res.rev);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [draft, confirmed, projectId, onConfirmed]);

  // Konfidenz als grober Balken (0..1) für die schnelle visuelle Einordnung.
  const confidencePct = (c: number): string => `${Math.round(Math.max(0, Math.min(1, c)) * 100)}%`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('plant.uaBrowse.title')}
      data-testid="ua-browse-dialog"
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col gap-4 overflow-y-auto rounded-lg border border-bf-border bg-bf-navy p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-bf-white">{t('plant.uaBrowse.title')}</h2>
          <button
            type="button"
            aria-label={t('common.close')}
            data-testid="ua-browse-close"
            onClick={onClose}
            className="text-bf-muted hover:text-bf-white"
          >
            ✕
          </button>
        </div>

        <p className="text-xs text-bf-muted">{t('plant.uaBrowse.intro')}</p>

        {/* Schritt 1: Endpunkt + Browse. */}
        {!draft && (
          <div className="flex flex-col gap-3" data-testid="ua-browse-step-endpoint">
            <LabeledInput
              label={t('plant.uaBrowse.endpoint')}
              value={endpoint}
              onChange={setEndpoint}
              placeholder="opc.tcp://plc:4840"
            />
            <div
              className="rounded border border-bf-border bg-bf-navy-light/40 p-2 text-xs text-bf-muted"
              data-testid="ua-browse-readonly-note"
            >
              {t('plant.uaBrowse.readOnlyNote')}
            </div>
            <div className="flex justify-end">
              <Button
                variant="primary"
                disabled={busy || endpoint.trim().length < 'opc.tcp://x'.length}
                onClick={() => void runBrowse()}
                testId="ua-browse-start"
              >
                {busy ? t('plant.uaBrowse.browsing') : t('plant.uaBrowse.browse')}
              </Button>
            </div>
          </div>
        )}

        {/* Schritt 2: Review der Kandidaten. */}
        {draft && !savedRev && (
          <div className="flex flex-col gap-3" data-testid="ua-browse-step-review">
            <div className="flex items-center justify-between">
              <PanelHeading>{t('plant.uaBrowse.candidates')}</PanelHeading>
              <span className="text-xs text-bf-muted" data-testid="ua-browse-count">
                {t('plant.uaBrowse.confirmedCount', {
                  confirmed: confirmedCount,
                  total: draft.catalog.variables.length,
                })}
              </span>
            </div>

            {draft.catalog.variables.length === 0 && (
              <p className="text-xs text-amber-300" data-testid="ua-browse-empty">
                {t('plant.uaBrowse.empty')}
              </p>
            )}

            <ul className="flex flex-col gap-1" data-testid="ua-browse-list">
              {draft.catalog.variables.map((v) => {
                const row = draft.review.tags.find((r) => r.tag === v.tag);
                const isOn = confirmed[v.tag] ?? false;
                return (
                  <li
                    key={v.tag}
                    className={`flex items-center gap-3 rounded border p-2 ${
                      isOn ? 'border-bf-gold/50 bg-bf-navy-light/40' : 'border-bf-border opacity-60'
                    }`}
                    data-testid={`ua-browse-row-${v.tag}`}
                  >
                    <input
                      type="checkbox"
                      checked={isOn}
                      onChange={() => toggle(v.tag)}
                      aria-label={t('plant.uaBrowse.confirmTag', { tag: v.tag })}
                      data-testid={`ua-browse-check-${v.tag}`}
                      className="h-4 w-4 accent-bf-gold"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium text-bf-white" title={v.name}>
                          {v.name}
                        </span>
                        <span className="rounded bg-bf-border px-1.5 py-0.5 text-[10px] text-bf-muted">
                          {v.direction}
                        </span>
                      </div>
                      <div className="truncate text-[11px] text-bf-muted" title={v.opc?.node_id}>
                        {v.opc?.node_id} · {v.opc?.datatype} · {v.unit}
                      </div>
                    </div>
                    {row && (
                      <span
                        className="shrink-0 text-[11px] text-bf-muted"
                        title={t('plant.uaBrowse.confidence')}
                        data-testid={`ua-browse-conf-${v.tag}`}
                      >
                        {confidencePct(row.confidence)}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>

            {/* Offene Punkte aus dem Review-Overlay (Größe/Grenzen im Review zu setzen). */}
            <div className="rounded border border-bf-border p-2" data-testid="ua-browse-open-points">
              <PanelHeading>{t('plant.uaBrowse.openPoints')}</PanelHeading>
              <ul className="mt-1 list-disc pl-4 text-[11px] text-bf-muted">
                {draft.review.open_points.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Erfolg. */}
        {savedRev && (
          <div
            className="rounded border border-green-500/50 bg-green-950/20 p-2 text-sm text-green-300"
            data-testid="ua-browse-done"
          >
            {t('plant.uaBrowse.saved', { rev: savedRev })}
          </div>
        )}

        {error && (
          <p className="text-xs text-red-300" data-testid="ua-browse-error">
            {error}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2">
          {draft && !savedRev && (
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => void runConfirm()}
              testId="ua-browse-confirm"
            >
              {busy
                ? t('plant.uaBrowse.saving')
                : t('plant.uaBrowse.confirm', { confirmed: confirmedCount })}
            </Button>
          )}
          <Button onClick={onClose} testId="ua-browse-cancel">
            {savedRev ? t('common.close') : t('plant.uaBrowse.cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
}
